import { forwardRef, type Ref, type ReactNode, type CSSProperties } from 'react';
import { Loader } from '../../core/Loader/Loader.js';
import './LoadingOverlay.css';

export interface LoadingOverlayProps {
  /** When true, the overlay is mounted and visible. */
  visible: boolean;
  /** Custom loader content (replaces default `<Loader>`). */
  loader?: ReactNode;
  /** Optional label rendered below the spinner. */
  label?: ReactNode;
  /** Loader variant when using the default `<Loader>`. */
  loaderVariant?: 'oval' | 'bars' | 'dots';
  /** Loader size when using the default. */
  loaderSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Background blur in px. Default 2. */
  blur?: number;
  /** Background tint opacity 0..1. Default 0.55. */
  backgroundOpacity?: number;
  /** Background color override. */
  color?: string;
  zIndex?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Absolutely-positioned overlay used to mask a parent surface while it's
 * loading. Place inside any `position: relative` ancestor: typical
 * usages are around `<Card>`, `<DataTable>`, or a form section.
 */
export const LoadingOverlay = forwardRef(function LoadingOverlay(
  {
    visible, loader, label,
    loaderVariant = 'oval', loaderSize = 'md',
    blur = 2, backgroundOpacity = 0.55, color, zIndex,
    className, style
  }: LoadingOverlayProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  if (!visible) return null;
  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      className={['rr-loading-overlay', className].filter(Boolean).join(' ')}
      style={{
        ...style,
        ['--rr-overlay-blur' as string]: `${blur}px`,
        ['--rr-overlay-bg' as string]: color ?? `rgba(255,255,255,${backgroundOpacity})`,
        zIndex
      }}
    >
      <div className="rr-loading-overlay__inner">
        {loader ?? <Loader variant={loaderVariant} size={loaderSize} />}
        {label ? <div className="rr-loading-overlay__label">{label}</div> : null}
      </div>
    </div>
  );
});
